import { useMutation, useQueryClient } from '@tanstack/react-query';

import { unlinkLineChannel } from 'api/line';
import { queryKeys } from 'constants/queryKeys';
import { lineStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import { toErrorMessage } from '../utils';

type UseUnlinkLineChannelOptions = {
  onSuccess?: () => void;
};

type UseUnlinkLineChannelResult = {
  unlink: (lineChannelId: number) => void;
  isSubmitting: boolean;
  error: string | undefined;
  resetError: () => void;
};

export function useUnlinkLineChannel(
  options: UseUnlinkLineChannelOptions = {},
): UseUnlinkLineChannelResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = lineStrings.management;

  const mutation = useMutation({
    mutationFn: async (lineChannelId: number) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return unlinkLineChannel(lineChannelId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_CHANNEL_ACCOUNTS, user?.uid],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_FRIENDS, user?.uid],
      });
      options.onSuccess?.();
    },
  });

  const unlink = (lineChannelId: number) => {
    mutation.mutate(lineChannelId);
  };

  const error = mutation.error
    ? toErrorMessage(mutation.error, strings.errors.unlinkFailed)
    : undefined;

  return {
    unlink,
    isSubmitting: mutation.isPending,
    error,
    resetError: mutation.reset,
  };
}
